import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { EventType } from '@azure/msal-browser';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { msalInstance, loginRequest, isAllowedDomain } from '../config/authConfig';

// Initialize MSAL (using shared instance from authConfig)
msalInstance.initialize().then(() => {
  // Handle redirect promise
  msalInstance.handleRedirectPromise().catch((error) => {
    console.error('Redirect error:', error);
  });

  // Set active account if available
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  // Listen for sign-in events
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
      msalInstance.setActiveAccount(event.payload.account);
    }
  });
});

// Auth Context
const AuthContext = createContext(null);

/**
 * Hook to access auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Inner auth provider that uses MSAL hooks
 */
function AuthProviderInner({ children }) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get current account
  const account = accounts[0] || null;

  // Update user state when account changes
  useEffect(() => {
    if (account) {
      const email = account.username;

      // Validate domain
      if (!isAllowedDomain(email)) {
        setError(`Access denied. Only @codvo.ai emails are allowed.`);
        instance.logoutRedirect();
        return;
      }

      setUser({
        id: account.localAccountId,
        name: account.name,
        email: email,
        username: account.username,
      });
      setError(null);
    } else {
      setUser(null);
    }
    setLoading(false);
  }, [account, instance]);

  // Login function
  const login = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [instance]);

  // Logout function
  const logout = useCallback(async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (err) {
      console.error('Logout error:', err);
    }
  }, [instance]);

  // Get access token
  const getAccessToken = useCallback(async () => {
    if (!account) return null;

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });
      return response.accessToken;
    } catch (err) {
      console.error('Token acquisition error:', err);
      // If silent acquisition fails, try redirect
      await instance.acquireTokenRedirect(loginRequest);
      return null;
    }
  }, [instance, account]);

  const value = {
    user,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    getAccessToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Main Auth Provider component
 */
export function AuthProvider({ children }) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProviderInner>
        {children}
      </AuthProviderInner>
    </MsalProvider>
  );
}

export default AuthProvider;
